import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import * as K from "../../lib/const.js";
import * as api from "../../lib/api.js";
import * as chirp from "../../lib/chirp.js";
import { t } from "../contract/strings";
import { Latch } from "../engine/latch";
import { createSession, isSetupAbort, joinSession, newChirpContext } from "../engine/session";
import {
  ensureModelReady,
  isAbort,
  isModelReady,
  ModelLoadError,
  warmModel,
} from "../engine/model-readiness";
import { sessionStartFailure } from "../engine/session-errors";
import { aiConsentRefusal, demandAiConsent } from "../engine/ai-consent";
import {
  OtherMeetingError,
  otherMeetingAction,
  otherMeetingMessage,
  otherMeetingRefusal,
  resolveOtherMeeting,
  type OtherMeeting,
  type OtherMeetingPorts,
} from "../engine/other-meeting";
import { streamProbeDebugEnabled } from "../engine/audio-uplink";
import { recordingPaused } from "../engine/server-config";
import { refreshAccount } from "../engine/signin";
import { recordingIdentityBlock, useApp, type AppState } from "../state/store";
import {
  AudioNotice,
  Button,
  CodeInput,
  ModelProgress,
  OrDivider,
  Overlay,
  SkeletonGate,
  SkeletonLine,
  StatusLine,
  cx,
} from "../ui";
import {
  ArrowRightIcon,
  EqBarsIcon,
  KeyIcon,
  KeyboardIcon,
  MicIcon,
  PersonIcon,
  SettingsIcon,
} from "../ui/icons";
import { Screen } from "./screen";
import {
  openPastSession,
  PastSessionCard,
  PastSessionCardSkeleton,
  type PastSession,
} from "./past-sessions";
import { useScreenStatus } from "./use-screen-status";
import {
  billingRefusal,
  blockedPlateCopy,
  blockedPlateTarget,
  useWallet,
  type StartRefusal,
} from "./wallet";
import { captureAnalytics } from "../analytics";
import { launchRecentRead, RECENT_LIMIT } from "../engine/home-data";
/** Which overlay, if any, holds the screen. One at a time; each is a whole task. */
type HomeOverlay = "chirp" | "code" | null;

/**
 * The recent strip's rows (#1049). It lives in `engine/home-data.ts` because the launch
 * starts this same read (#1066); re-exported where the screen and the gallery name it.
 */
export { RECENT_LIMIT };

/** What End it or Leave it reads and calls, from the real API and the real clock. */
const OTHER_MEETING_PORTS: OtherMeetingPorts = {
  endSession: (id) => api.endSession(id),
  leaveSession: (id) => api.leaveOtherMeeting(id),
  getSession: (id) => api.getSession(id),
  wallet: () => api.wallet(),
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  now: () => Date.now(),
};

/**
 * Home: start one, or join one.
 *
 * The two verbs are plates on the page rather than a form: the record card is the one
 * thing a host needs and takes the width it deserves; joining splits into its two equal
 * senses -- ears (the chirp) or hands (the code) -- as tiles beside each other. Each door
 * opens a full-screen overlay, because joining wants attention for a few seconds and then
 * to hand back a result. Starting takes no overlay: the plate starts the meeting on the
 * tap, unnamed (#708).
 *
 * Signed in only: the route guard sends a signed-out device to the front door.
 */
export function Home() {
  const navigate = useNavigate();
  const streamDebug = streamProbeDebugEnabled();
  const account = useApp((s) => s.account);
  const credentials = useApp((s) => s.credentials);
  /** Why recording/joining is refused right now, or null. See `recordingIdentityBlock`. */
  const linkBlock = useApp(recordingIdentityBlock);
  /** The deployment takes no audio (#388): every verb that records is off, and `AudioNotice` says why. */
  const paused = useApp(recordingPaused);
  const [status, say] = useScreenStatus();
  const [overlay, setOverlay] = useState<HomeOverlay>(null);

  const [code, setCode] = useState("");
  /**
   * A start in flight. The plate starts the meeting on the tap itself, so a second tap
   * before React re-renders the disabled plate must not start a second meeting -- the
   * same reason `joining` below is a ref. `creating` only paints the plate.
   */
  const starting = useRef(new Latch());
  const [creating, setCreating] = useState(false);
  const [listening, setListening] = useState(false);
  /**
   * A join in flight. Both doors are disabled while one is, because the chirp listener
   * reaches the same code path -- see `Latch` for what the second tap used to do.
   *
   * A ref, not state: the guard has to hold on the tap that arrives before React has
   * re-rendered, which is exactly the tap it exists for. The boolean beside it is only for
   * painting the buttons.
   */
  const joining = useRef(new Latch());
  const [joiningNow, setJoiningNow] = useState(false);
  const listener = useRef<{ stop: () => void } | null>(null);
  /** The last code this overlay auto-submitted, so an edit that re-renders cannot resubmit it. */
  const attempted = useRef<string>("");
  /** Whether this opening of the chirp overlay already started its listener. */
  const chirpArmed = useRef(false);
  /**
   * The two most recent sessions, for the history strip at the bottom of the page.
   * `null` while unasked or unanswerable; an empty array means signed in with no
   * history, and the strip stays hidden either way -- home's job is starting and
   * joining, and an empty list is noise beside it.
   */
  const [recent, setRecent] = useState<PastSession[] | null>(null);
  /** The strip's read failed: it stays hidden, quietly, as it always has. */
  const [recentFailed, setRecentFailed] = useState(false);
  /**
   * Which verb is waiting on the voice model (#658): "Getting ready…" with its progress,
   * or the error with Try again, in place of that verb's button. Held after a failure so
   * the error stays up until Try again or Cancel. In the store so the gallery can seed it.
   */
  const modelWait = useApp((s) => s.modelWait);
  /** Whether this screen set `modelWait`, so leaving clears its own and nobody else's. */
  const ownsWait = useRef(false);
  function setModelWait(wait: "create" | "join" | null) {
    const previous = useApp.getState().modelWait;
    // Once per entry: a retry, a re-render or another verb while it is up is not a new view.
    if (wait !== null && previous === null) {
      captureAnalytics("dialog_viewed", { dialog: "model_wait" });
    }
    ownsWait.current = wait !== null;
    useApp.setState({ modelWait: wait });
  }
  /**
   * The wait ended without the verb starting -- Cancel, or Home left while it was still
   * up. That is an explicit exit, so it is a dismissal (#1085). A wait that finishes and
   * starts the verb is a completion, and clears itself through [setModelWait].
   */
  function dismissModelWait() {
    if (!ownsWait.current) return;
    ownsWait.current = false;
    captureAnalytics("dialog_dismissed", { dialog: "model_wait" });
    useApp.setState({ modelWait: null });
  }
  /**
   * Cancels a Start or Join until the session screen takes it over (`onSetup`). Leaving
   * Home before then cancels it too, so a load that finishes later starts nothing.
   */
  const pendingStart = useRef<AbortController | null>(null);
  /** The last join asked for, so Try again after a failed load repeats it. */
  const lastJoin = useRef<{ code: string; method: string } | null>(null);
  /**
   * Why a new meeting would be refused (#686), so the record plate can say so before the
   * host names one: the wallet's own answer, or the code a create just came back with.
   * Joining is never billed, so the doors below are untouched.
   */
  const { wallet, reload: reloadWallet } = useWallet(!!account);
  const [refused, setRefused] = useState<StartRefusal | null>(null);
  const startBlock = refused ?? wallet?.start_refusal ?? null;
  const blockedCopy = startBlock ? blockedPlateCopy(startBlock, wallet) : null;
  /**
   * A start or join refused over another live meeting (#735), and End it or Leave it in
   * flight. `retryOther` is what to do once that meeting is out of the way: the start, or
   * the same join again. `resolving` cancels the wait when Home goes away.
   */
  const otherMeeting = useApp((s) => s.otherMeeting) ?? null;
  const otherBusy = otherMeeting?.busy != null;
  const retryOther = useRef<{ verb: "create" } | { verb: "join"; code: string; method: string } | null>(null);
  const resolving = useRef<AbortController | null>(null);
  /** Whether this screen set `otherMeeting`, so leaving clears its own and not a seeded one. */
  const ownsOther = useRef(false);
  function setOtherMeeting(next: AppState["otherMeeting"]): void {
    ownsOther.current = next !== null;
    useApp.setState({ otherMeeting: next });
  }

  /** Whether Home is still up, so a start or join retried after the AI agreement is not orphaned. */
  const mounted = useRef(true);

  // Home is where the model gets ready in the background (#659). Boot no longer waits for
  // it, so this is the earliest a signed-in, set-up device asks.
  useEffect(() => {
    mounted.current = true;
    void warmModel();
    return () => {
      mounted.current = false;
      pendingStart.current?.abort();
      dismissModelWait();
      // A refusal belongs to this visit: coming back to Home starts clean.
      resolving.current?.abort();
      if (ownsOther.current) useApp.setState({ otherMeeting: null });
    };
  }, []);

  /** Stop waiting for the model; the load itself carries on for next time. */
  function cancelModelWait() {
    pendingStart.current?.abort();
    pendingStart.current = null;
    dismissModelWait();
  }

  // History belongs to the account: a signed-out device has none to show. Two rows
  // only -- the archive at /sessions has the rest. A load failure is deliberately
  // quiet here: home's status line speaks for the verbs above, not for this. The read the
  // launch started is used while it is fresh rather than asked again (#1066).
  useEffect(() => {
    if (!account) return;
    let alive = true;
    (launchRecentRead() ?? api.accountSessions({ limit: RECENT_LIMIT })).then(
      (rows: PastSession[]) => alive && setRecent(rows),
      () => alive && setRecentFailed(true)
    );
    return () => {
      alive = false;
    };
  }, [account]);


  /**
   * `chirpCtx` is for a start that follows End it: that tap is the user activation the
   * chirp's audio needs, and the wait for the other meeting to end outlasts it.
   */
  async function create(chirpCtx?: AudioContext | null) {
    const block = recordingIdentityBlock(useApp.getState());
    if (block !== null) {
      say(t("home.error_account_start"), "err");
      navigate("/account");
      return;
    }
    if (!starting.current.tryEnter()) return;
    setCreating(true);
    // A new attempt answers for itself: a refusal it meets brings the panel back.
    setOtherMeeting(null);
    const tappedAt = performance.now();
    // Before the first await, while this click still counts as user activation -- see
    // `newChirpContext`. The session that follows starts broadcasting immediately, because
    // the moment a code exists is exactly when people are trying to join.
    const ctx = chirpCtx === undefined ? newChirpContext() : chirpCtx;
    const abort = new AbortController();
    pendingStart.current = abort;
    try {
      // The model first, before any session exists: a Cancel here leaves nothing behind.
      if (!isModelReady()) setModelWait("create");
      await ensureModelReady({ signal: abort.signal });
      setModelWait(null);
      await createSession(ctx, {
        signal: abort.signal,
        tappedAt,
        // The session exists: its screen says "Setting up the meeting…" until the first
        // audio, and owns the Cancel from here.
        onSetup: () => {
          pendingStart.current = null;
          navigate(streamDebug ? "/session?stream-probe=1" : "/session");
        },
      });
    } catch (err) {
      try {
        await ctx?.close();
      } catch {
        /* Never opened, or already gone. */
      }
      // A Cancel is not a failure, and a model that would not load is already on screen
      // with its own Try again.
      if (isAbort(err) || isSetupAbort(err)) return;
      if (err instanceof ModelLoadError) return;
      setModelWait(null);
      console.warn("createSession failed", err);
      // Refused for billing: the plate takes the blocked state, read fresh.
      const refusal = billingRefusal(err);
      if (refusal) {
        setRefused(refusal);
        void reloadWallet();
      }
      // Another meeting is still live and the refusal says which: Home says where it is
      // running and offers End it or Leave it, then starts (#735).
      const other = otherMeetingRefusal(err);
      if (other) {
        showOtherMeeting(other, { verb: "create" });
        return;
      }
      if (askForAiConsent(err, { verb: "create" })) return;
      const failure = sessionStartFailure(err, true);
      say(t(failure.key, failure.vars), "err");
    } finally {
      if (pendingStart.current === abort) pendingStart.current = null;
      setCreating(false);
      starting.current.release();
    }
  }

  async function join(joinCode: string, method: string) {
    // Re-checked here, not only on the disabled buttons: the chirp listener and the
    // typed-code effect both reach `join` without a click, so a disabled tile is not a
    // gate (#38).
    const block = recordingIdentityBlock(useApp.getState());
    if (block !== null) {
      say(t("home.error_account_join"), "err");
      navigate("/account");
      return;
    }
    await joining.current.run(async () => {
      setJoiningNow(true);
      setOtherMeeting(null);
      lastJoin.current = { code: joinCode, method };
      const tappedAt = performance.now();
      const abort = new AbortController();
      pendingStart.current = abort;
      try {
        // The code (or the chirp) is in hand; joining waits for the model, before the
        // server is asked, so a Cancel here joins nothing (#658).
        if (!isModelReady()) setModelWait("join");
        await ensureModelReady({ signal: abort.signal });
        setModelWait(null);
        say(t("home.joining_code", { code: joinCode }));
        await joinSession(joinCode, method, {
          signal: abort.signal,
          tappedAt,
          onSetup: () => {
            pendingStart.current = null;
            navigate(streamDebug ? "/session?stream-probe=1" : "/session");
          },
        });
      } catch (err) {
        if (isAbort(err) || isSetupAbort(err)) return;
        if (err instanceof ModelLoadError) return;
        setModelWait(null);
        console.warn("joinSession failed", err);
        // This device is still in another meeting: the overlay hands back the page, which
        // offers End it or Leave it and then joins this code (#735).
        const other = otherMeetingRefusal(err);
        if (other) {
          attempted.current = "";
          setOverlay(null);
          captureAnalytics("dialog_dismissed", { dialog: "join_code" });
          showOtherMeeting(other, { verb: "join", code: joinCode, method });
          return;
        }
        if (askForAiConsent(err, { verb: "join", code: joinCode, method })) {
          // The page under the sheet, so the join that follows Agree is seen from Home.
          attempted.current = "";
          setOverlay(null);
          captureAnalytics("dialog_dismissed", { dialog: "join_code" });
          return;
        }
        const failure = sessionStartFailure(err, false);
        say(t(failure.key, failure.vars), "err");
      } finally {
        if (pendingStart.current === abort) pendingStart.current = null;
        // Released even on success, when the session screen is already showing: leaving
        // them disabled would hand the next visit to home a pair of dead buttons.
        setJoiningNow(false);
      }
    });
  }

  /**
   * A start or join refused for want of the AI agreement (#915), e.g. the wording changed
   * while this page was open: "How MeetFolks uses AI" comes up, and once the person agrees
   * the same start, or the same join code, goes ahead. False when `err` is not that
   * refusal, or nobody is signed in to agree, which then reads as any other refusal.
   */
  function askForAiConsent(
    err: unknown,
    retry: NonNullable<typeof retryOther.current>
  ): boolean {
    const refusal = aiConsentRefusal(err);
    if (!refusal || !useApp.getState().account) return false;
    say("");
    demandAiConsent(
      refusal.version,
      (chirpCtx) => {
        if (!mounted.current) {
          void chirpCtx?.close().catch(() => {});
          return;
        }
        if (retry.verb === "join") void join(retry.code, retry.method);
        else void create(chirpCtx);
      },
      retry.verb === "create"
    );
    return true;
  }

  /** Show a refusal over another live meeting, and remember what to do once it is out of the way. */
  function showOtherMeeting(
    meeting: OtherMeeting,
    retry: NonNullable<typeof retryOther.current>
  ): void {
    retryOther.current = retry;
    // The panel is the message: the status line would only say it a second time.
    say("");
    captureAnalytics("dialog_viewed", { dialog: "other_meeting" });
    setOtherMeeting({ meeting, busy: null, slow: false });
  }

  /**
   * End it or Leave it, then carry on with the start or the join it held up (#735).
   *
   * End it stops the meeting for everyone in it, so it asks first. While it waits for the
   * meeting to stop blocking, the panel says so (and, past a few seconds, that it can take
   * minutes); both verbs stay disabled. A refusal leaves the panel up with the reason on
   * the status line, so the person can try again.
   */
  async function resolveOther() {
    const current = useApp.getState().otherMeeting;
    if (!current || current.busy) return;
    const action = otherMeetingAction(current.meeting);
    if (action === "end" && !window.confirm(t("home.end_confirm"))) return;
    // This tap is the user activation a start's chirp needs; the wait below outlasts it.
    const ctx = retryOther.current?.verb === "create" ? newChirpContext() : null;
    const abort = new AbortController();
    resolving.current = abort;
    say("");
    setOtherMeeting({ ...current, busy: action, slow: false });
    try {
      await resolveOtherMeeting(current.meeting, action, OTHER_MEETING_PORTS, {
        signal: abort.signal,
        onSlow: () => {
          const now = useApp.getState().otherMeeting;
          if (now) setOtherMeeting({ ...now, slow: true });
        },
      });
    } catch (err) {
      try {
        await ctx?.close();
      } catch {
        /* Never opened, or already gone. */
      }
      if (abort.signal.aborted) return;
      console.warn("ending or leaving the other meeting failed", err);
      setOtherMeeting({ ...current, busy: null, slow: false });
      const failure =
        err instanceof OtherMeetingError
          ? err
          : new OtherMeetingError(action === "end" ? "home.error_end" : "home.error_leave");
      say(t(failure.key, failure.vars), "err");
      return;
    } finally {
      if (resolving.current === abort) resolving.current = null;
    }
    const next = retryOther.current;
    retryOther.current = null;
    setOtherMeeting(null);
    captureAnalytics("other_meeting_resolved", { action });
    captureAnalytics("dialog_dismissed", { dialog: "other_meeting" });
    // The plate's blocked state came from the refusal or a wallet read from before.
    setRefused(null);
    void reloadWallet();
    if (next?.verb === "join") void join(next.code, next.method);
    else void create(ctx);
  }

  /** Try again after the model failed to load for a join: the same code, the same way. */
  function retryJoin() {
    captureAnalytics("retry_tapped", { surface: "model_load" });
    const last = lastJoin.current;
    if (last) void join(last.code, last.method);
  }

  function joinTyped() {
    const value = code.trim().toUpperCase();
    if (value.length !== K.JOIN_CODE_LENGTH) {
      say(t("home.code_length", { count: K.JOIN_CODE_LENGTH }), "err");
      return;
    }
    void join(value, "typed");
  }

  async function listen() {
    if (listener.current) {
      listener.current.stop();
      listener.current = null;
      setListening(false);
      return;
    }
    setListening(true);
    say(t("home.looking_nearby"));
    try {
      const handle = chirp.listen({
        onTone: () => say(t("home.looking_nearby")),
      });
      listener.current = handle;
      const heard = await handle.code;
      listener.current = null;
      setListening(false);
      if (!heard) {
        // Not an error, and deliberately not styled as one: ~1 in 10 devices cannot receive
        // at all, and the code beside this button always works.
        say(t("home.no_meeting"), "");
        return;
      }
      setCode(heard);
      // Hand back the screen before joining: the answer arrived, the sensor's work is done.
      setOverlay((o) => (o === "chirp" ? null : o));
      await join(heard, "chirp");
    } catch (err) {
      listener.current = null;
      setListening(false);
      console.warn("chirp listen failed", err);
      say(t("home.error_chirp"), "");
    }
  }

  /**
   * The one way out of any overlay, and the only one: closing a chirp stops its listener.
   * Escape and the header button both land here.
   */
  function closeOverlay() {
    // Closing the overlay a join is waiting in cancels the wait (#658). A start waits on
    // the page, not in an overlay, so closing one leaves it alone.
    if (modelWait === "join" && overlay === "code") cancelModelWait();
    if (overlay === "chirp") {
      chirpArmed.current = false;
      if (listener.current) {
        listener.current.stop();
        listener.current = null;
        setListening(false);
      }
    }
    if (overlay === "code") attempted.current = "";
    if (overlay !== null) {
      captureAnalytics("dialog_dismissed", {
        dialog: overlay === "chirp" ? "join_chirp" : "join_code",
      });
    }
    setOverlay(null);
  }

  // Opening the chirp overlay arms the sensor; nothing else does.
  useEffect(() => {
    if (overlay !== "chirp" || chirpArmed.current) return;
    chirpArmed.current = true;
    void listen();
    // `listen` is the armed behaviour itself; the guard above makes once mean once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay]);

  // A completed code joins by itself: five characters are a complete sentence, and the
  // overlay's loading state is the reply. The ref keeps one code to one attempt.
  useEffect(() => {
    if (overlay !== "code") return;
    const value = code.trim().toUpperCase();
    if (value.length !== K.JOIN_CODE_LENGTH || attempted.current === value || joiningNow) return;
    attempted.current = value;
    void join(value, "typed");
    // `join` runs the attempt; `joiningNow` gates re-entry while it is in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, overlay]);

  return (
    <Screen name="home">
      {/* The greeting, and the two ways off home -- no card around it: the page itself
          is the container, and the squircles are the only chrome the row needs. */}
      <div className="flex items-center justify-between my-4">
        <p className="m-0 text-who font-semibold">
          {credentials?.displayName ? t("home.welcome_named", { name: credentials.displayName as string }) : t("home.welcome")}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={t("home.account")}
            onClick={() => navigate("/account")}
            className="size-icon-button rounded-squircle border border-line-strong bg-surface text-accent flex items-center justify-center button-transition hover:opacity-90 enabled:active:button-press"
          >
            <PersonIcon className="size-icon" />
          </button>
          <button
            type="button"
            aria-label={t("home.settings")}
            onClick={() => navigate("/settings")}
            className="size-icon-button rounded-squircle border border-line-strong bg-surface text-accent flex items-center justify-center button-transition hover:opacity-90 enabled:active:button-press"
          >
            <SettingsIcon className="size-icon" />
          </button>

        </div>
      </div>

      {/* Above both verbs, because it changes what starting one means: a deployment that
          takes no audio disables them (#388), and a device that could not read the
          deployment's settings is told so. Renders nothing when there is nothing wrong --
          see `AudioNotice`. */}
      <AudioNotice />

      {/* Above both verbs, because it changes what either one means: while the account
          link is unresolved the engine refuses to record, so the tiles are disabled and
          this is why. Retrying re-runs the boot check, which also resets a device that
          belongs to another account (#336). */}
      {linkBlock !== null && (
        <div className="my-4 rounded-panel border border-line bg-surface-2 p-pad">
          <p className="m-0 text-lede">
            {linkBlock === "checking"
              ? t("home.checking_account")
              : t("home.account_paused")}
          </p>
          <Button
            variant="link"
            className="mt-2"
            onClick={() => {
              captureAnalytics("retry_tapped", { surface: "account_link" });
              void refreshAccount();
            }}
            disabled={linkBlock === "checking"}
          >
            {t("common.try_again")}
          </Button>
        </div>
      )}

      {/* The record plate: the host's whole job in one filled card. The mic names it, the
          arrow says it goes somewhere, and the line underneath says what you get. Filled
          because it is the page's primary action -- the join doors beside it stay quiet
          outlines so the eye lands here first. `border-none`: without preflight an unstyled
          button wears the browser's own grey outset border (#613).
          Tapping it starts the meeting at once: a meeting starts unnamed and is shown by its
          start time until its summary or its creator names it (#708). While it starts, the
          plate says so, and a wait for the voice model shows just below it.
          Blocked for billing (#686), the same plate recolours to the danger fill a danger
          Button uses and says why, in the cause's own words; it stays a live control and
          goes to that cause's remedy instead of starting. A meeting still running has no
          other screen for its remedy: the plate tries the start, and the refusal brings up
          End it below (#735). */}
      <button
        type="button"
        onClick={() => {
          const target = startBlock ? blockedPlateTarget(startBlock, wallet) : null;
          if (target) navigate(target);
          else void create();
        }}
        disabled={creating || joiningNow || otherBusy || linkBlock !== null || paused}
        className={cx(
          "flex w-full flex-col gap-2 text-left my-4 rounded-panel border-none text-accent-ink p-plate button-transition enabled:hover:opacity-90 enabled:active:button-press disabled:opacity-45",
          blockedCopy ? "bg-danger" : "bg-accent"
        )}
      >
        <span className="flex w-full items-center justify-between">
          <MicIcon className="size-icon-xl" />
          <ArrowRightIcon className="size-icon-md" />
        </span>
        <span className="block mt-4">
          <span className="block font-title text-stat font-bold tracking-headline">
            {blockedCopy ? blockedCopy.title : creating ? t("home.starting") : t("home.start_conversation")}
          </span>
          {blockedCopy ? <span className="block mt-1 text-lede opacity-80">{blockedCopy.subtitle}</span> : null}
        </span>
      </button>

      {/* Start is waiting for the voice model (#658): its progress, or the error with Try
          again, under the plate that started it. */}
      {modelWait === "create" ? (
        <ModelProgress className="my-4" onRetry={() => {
          captureAnalytics("retry_tapped", { surface: "model_load" });
          void create();
        }} onCancel={cancelModelWait} />
      ) : null}

      {/* A start or a join refused over another meeting that is still live (#735): what is
          running and where, in a panel like the account-link one above, with the one thing
          that clears it. End it for a meeting this account pays for, Leave it for anyone
          else's. While it works, the panel says so and the verbs wait; a failure stays in
          the panel, so the page's status line steps aside for it. */}
      {otherMeeting !== null && (
        <div className="my-4 rounded-panel border border-line bg-surface-2 p-pad">
          <p className="m-0 text-lede">{t(otherMeetingMessage(otherMeeting.meeting))}</p>
          {otherMeeting.busy !== null ? (
            <p className="m-0 mt-2 text-note text-ink-soft" aria-live="polite">
              {otherMeeting.busy === "leave"
                ? t("home.leaving_other_meeting")
                : otherMeeting.slow
                  ? t("home.ending_other_meeting_slow")
                  : t("home.ending_other_meeting")}
            </p>
          ) : status.message ? (
            <p
              className={cx("m-0 mt-2 text-note", status.kind === "err" && "text-danger")}
              role={status.kind === "err" ? "alert" : undefined}
            >
              {status.message}
            </p>
          ) : null}
          <Button
            variant="ghost"
            danger={otherMeetingAction(otherMeeting.meeting) === "end"}
            className="mt-gutter"
            disabled={otherBusy || creating || joiningNow}
            onClick={() => void resolveOther()}
          >
            {otherMeetingAction(otherMeeting.meeting) === "end" ? t("home.end_it") : t("home.leave_it")}
          </Button>
        </div>
      )}

      {/* The page's two acts take a rule between them. The rule carries its own top
          margin -- the plate's bottom margin alone read as glued -- and the act label
          takes symmetric margins, so it sits centred in the gap with even air on both
          sides: plate, air, rule, air, label, air, doors. */}
      <hr className="mt-4" />

      {/* Joining, named as the second act and split into its two equal senses. */}
      <div className="flex items-center my-6 pl-1">
        <span className="font-code text-label font-bold uppercase tracking-label text-ink-soft">
          {t("home.join_existing")}
        </span>
      </div>

      {/* The divider sits between the tiles, never inside one of them -- equal paths,
          either one gets you in. Stacked it reads across; above 40rem it turns and runs
          down the gap. */}
      <div className="grid gap-4 doors-grid">
        <button
          type="button"
          onClick={() => {
            captureAnalytics("join_method_selected", { join_method: "chirp" });
            captureAnalytics("dialog_viewed", { dialog: "join_chirp" });
            setOverlay("chirp");
          }}
          disabled={creating || joiningNow || otherBusy || linkBlock !== null || paused}
          className="group rounded-panel border border-line bg-surface-2 p-pad flex flex-col items-center justify-center gap-gutter button-transition enabled:hover:border-line-strong enabled:active:button-press disabled:opacity-45"
        >
          {/* A quiet sensor: rings held inside the box's own bounds -- a hint of motion,
              not a broadcast. Decorative movement, off for anyone who asked not to be
              moved. */}
          <span className="relative size-tile flex items-center justify-center">
            <span
              className="absolute inset-0 rounded-squircle border border-accent animate-ping opacity-40 motion-reduce:animate-none"
              aria-hidden="true"
            />
            <span
              className="absolute inset-1.5 rounded-squircle border border-accent animate-ping-late opacity-25 motion-reduce:animate-none"
              aria-hidden="true"
            />
            <span className="relative z-10 size-tile rounded-squircle bg-surface-3 border border-line-strong text-accent flex items-center justify-center transition-colors group-hover:border-accent">
              <EqBarsIcon className="size-icon-lg" />
            </span>
          </span>
          <span className="text-ink font-medium text-center leading-row">
            {t("home.join_nearby")}
          </span>
        </button>

        <OrDivider placement="doors" />

        <button
          type="button"
          onClick={() => {
            captureAnalytics("join_method_selected", { join_method: "code" });
            captureAnalytics("dialog_viewed", { dialog: "join_code" });
            setOverlay("code");
          }}
          disabled={creating || joiningNow || otherBusy || linkBlock !== null || paused}
          className="group rounded-panel border border-line bg-surface-2 p-pad flex flex-col items-center justify-center gap-gutter button-transition enabled:hover:border-line-strong enabled:active:button-press disabled:opacity-45"
        >
          <span className="size-tile rounded-squircle bg-surface-3 border border-line-strong text-accent flex items-center justify-center transition-colors group-hover:border-accent">
            <KeyboardIcon className="size-icon-lg" />
          </span>
          <span className="text-ink font-medium text-center leading-row">
            {t("home.type_code")}
          </span>
        </button>
      </div>

      {/* The page's status line steps aside while an overlay holds the screen -- the
          overlay renders the same message itself, and two live regions would announce
          everything twice. Rendered only when there is something to say: an empty
          reserved line here is phantom air between the acts, and everything it used to
          protect sits above it anyway. */}
      {/* A join heard by the chirp closes its overlay, so its wait for the model is
          shown on the page (#658). */}
      {overlay === null && modelWait === "join" ? (
        <ModelProgress className="my-4" onRetry={retryJoin} onCancel={cancelModelWait} />
      ) : null}

      {overlay === null && otherMeeting === null && status.message ? (
        <StatusLine kind={status.kind}>{status.message}</StatusLine>
      ) : null}

      {/* The third act: where to find what you already made. Two rows and a way to the
          rest, after a rule like the one between starting and joining. Loading, it is a
          skeleton of the same (#1043), announced with Past meetings' own loading line;
          empty or failed, it stays hidden as before. */}
      <SkeletonGate
        loading={Boolean(account) && recent === null && !recentFailed}
        label={t("sessions.loading")}
        skeleton={
          <>
            <hr className="mt-4" />
            <div className="flex items-center justify-between my-6 pl-1">
              <SkeletonLine size="label" width="w-tile-lg" />
              <SkeletonLine size="link" width="w-tile-xs" className="p-link pr-1" />
            </div>
            <div className="grid gap-4">
              {Array.from({ length: RECENT_LIMIT }, (_, i) => (
                <PastSessionCardSkeleton key={i} />
              ))}
            </div>
          </>
        }
      >
        {recent && recent.length > 0 ? (
          <>
            <hr className="mt-4" />
            <div className="flex items-center justify-between my-6 pl-1">
              <span className="font-code text-label font-bold uppercase tracking-label text-ink-soft">
                {t("home.recent_sessions")}
              </span>
              <Button variant="link" className="pr-1" onClick={() => navigate("/sessions")}>
                {t("home.see_all")}
              </Button>
            </div>
            <div className="grid gap-4">
              {recent.map((session) => (
                <PastSessionCard
                  key={session.session_id}
                  session={session}
                  onOpen={(s) => openPastSession(s, navigate)}
                />
              ))}
            </div>
          </>
        ) : null}
      </SkeletonGate>

      {overlay === "chirp" ? (
        <Overlay label={t("home.join_nearby")} onClose={closeOverlay}>
          <div className="flex flex-col items-center text-center gap-gutter">
            {/* The listening dial: eight squircle rings radiating from under the meter
                mark, one every eighth of a second -- the sensor at full volume. Each is a
                literal class (dynamic insets are invisible to the compiler); the phase is
                a delay, which is time, not layout. Off for anyone who asked not to be
                moved. */}
            <span className="relative size-dial flex items-center justify-center">
              <span className="absolute inset-0 rounded-squircle border border-accent animate-ping opacity-50 motion-reduce:animate-none" style={{ animationDelay: "0ms" }} aria-hidden="true" />
              <span className="absolute inset-3 rounded-squircle border border-accent animate-ping opacity-50 motion-reduce:animate-none" style={{ animationDelay: "125ms" }} aria-hidden="true" />
              <span className="absolute inset-6 rounded-squircle border border-accent animate-ping opacity-45 motion-reduce:animate-none" style={{ animationDelay: "250ms" }} aria-hidden="true" />
              <span className="absolute inset-9 rounded-squircle border border-accent animate-ping opacity-45 motion-reduce:animate-none" style={{ animationDelay: "375ms" }} aria-hidden="true" />
              <span className="absolute inset-12 rounded-squircle border border-accent animate-ping opacity-40 motion-reduce:animate-none" style={{ animationDelay: "500ms" }} aria-hidden="true" />
              <span className="absolute inset-15 rounded-squircle border border-accent animate-ping opacity-40 motion-reduce:animate-none" style={{ animationDelay: "625ms" }} aria-hidden="true" />
              <span className="absolute inset-18 rounded-squircle border border-accent animate-ping opacity-35 motion-reduce:animate-none" style={{ animationDelay: "750ms" }} aria-hidden="true" />
              <span className="absolute inset-20 rounded-squircle border border-accent animate-ping opacity-30 motion-reduce:animate-none" style={{ animationDelay: "875ms" }} aria-hidden="true" />
              {/* The meter itself, live: five bars on the loader's breathe, each a beat
                  behind its neighbour. Off for anyone who asked not to be moved. */}
              <span className="relative z-10 size-tile-lg rounded-squircle bg-surface-2 border-2 border-line-strong text-accent flex items-center justify-center gap-1">
                <span className="w-1.5 h-4 rounded-pill bg-accent animate-eq-1 motion-reduce:animate-none" />
                <span className="w-1.5 h-8 rounded-pill bg-accent animate-eq-2 motion-reduce:animate-none" />
                <span className="w-1.5 h-12 rounded-pill bg-accent animate-eq-3 motion-reduce:animate-none" />
                <span className="w-1.5 h-8 rounded-pill bg-accent animate-eq-4 motion-reduce:animate-none" />
                <span className="w-1.5 h-4 rounded-pill bg-accent animate-eq-5 motion-reduce:animate-none" />
              </span>
            </span>
            <h2 className="m-0 normal-case">{listening ? t("home.listening") : t("home.search_stopped")}</h2>
            <p className="m-0 font-code text-count tracking-code text-ink-soft retrack-code">
              {t("home.ask_host_nearby")}
            </p>
            <p className="m-0 min-h-status-line text-note text-ink-soft">{status.message}</p>
            {!listening ? (
              <Button variant="ghost" onClick={() => void listen()}>
                {t("home.listen_again")}
              </Button>
            ) : null}
          </div>
        </Overlay>
      ) : null}

      {overlay === "code" ? (
        <Overlay label={t("home.enter_code")} onClose={closeOverlay}>
          <div className="flex flex-col items-center text-center gap-gutter w-full">
            <KeyIcon className="size-tile-md text-accent opacity-50" />
            <h2 className="m-0 normal-case">{t("home.enter_session_code")}</h2>
            <p className="m-0 text-note text-ink-soft">
              {t("home.ask_for_code", { count: K.JOIN_CODE_LENGTH })}
            </p>
            <div className="w-full max-w-80 mt-stack text-left">
              <CodeInput
                label={t("home.meeting_code")}
                hideLabel
                focusOnMount
                value={code}
                onValueChange={setCode}
              />
            </div>
            {modelWait === "join" ? (
              <ModelProgress onRetry={retryJoin} onCancel={cancelModelWait} />
            ) : joiningNow ? (
              <span className="flex gap-1 py-panel" aria-live="polite">
                {/* The reading: three bars, asking. Off for anyone who asked not to be
                    moved -- the status line below still speaks. */}
                <span className="w-1.5 h-6 bg-accent animate-pulse motion-reduce:animate-none" />
                <span className="w-1.5 h-6 bg-accent animate-pulse-mid motion-reduce:animate-none" />
                <span className="w-1.5 h-6 bg-accent animate-pulse-late motion-reduce:animate-none" />
              </span>
            ) : (
              <p
                className={cx(
                  "m-0 min-h-status-line text-note",
                  status.kind === "err" && "text-danger"
                )}
                role={status.kind === "err" ? "alert" : undefined}
              >
                {status.message || t("home.code_hint")}
              </p>
            )}
            {modelWait === "join" ? null : (
              <Button
                variant="ghost"
                className="mt-gutter w-full"
                disabled={creating || joiningNow || otherBusy || linkBlock !== null || paused}
                onClick={joinTyped}
              >
                {joiningNow ? t("home.joining") : t("home.join")}
              </Button>
            )}
          </div>
        </Overlay>
      ) : null}
    </Screen>
  );
}
